import React, { useCallback, useEffect, useState, useContext } from 'react';
import { FBMOrdersV2Api } from '../../../../api/fbmOrdersV2';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { UserContext } from '../../../../contexts/UserContext';

const CancelFBMOrderModal = ({ isOpen, onClose, onSuccess, onError, orderData }) => {
    const [cancelReason, setCancelReason] = useState('');
    const [allowReingest, setAllowReingest] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { fbmAccount } = useContext(UserContext);
    const fbmOrdersV2Api = new FBMOrdersV2Api();

    useEffect(() => {
        if (!(onSuccess instanceof Function)) {
            console.error('CancelFBMShipmentModal intialized incorrectly onSuccess is not a function');
        }
        if (!(onError instanceof Function)) {
            console.error('CancelFBMShipmentModal intialized incorrectly onError is not a function');
        }
        if (!(onClose instanceof Function)) {
            console.error('CancelFBMShipmentModal intialized incorrectly onClose is not a function');
        }
    }, [onClose, onSuccess, onError]);

    const handleCancelReasonChange = (event) => {
        setCancelReason(event.target.value);
    };

    const handleClose = () => {
        setCancelReason('');
        setAllowReingest(false);
        onClose();
    };

    const handleCancelOrder = useCallback(async () => {
        setIsLoading(true);
        if (!cancelReason.trim()) {
            onError('Please provide a reason for cancellation');
            setIsLoading(false);
            return;
        }
        const response = await fbmOrdersV2Api.cancelOrder(
            orderData.orderId,
            fbmAccount.fbmAccountName,
            cancelReason,
            allowReingest
        );
        if (response.status) {
            onSuccess(`Order ${orderData.orderId} cancelled successfully`);
            handleClose();
        } else {
            onError(response.msg || 'Failed to cancel order');
        }
        setIsLoading(false);
    }, [
        cancelReason,
        allowReingest,
        fbmAccount.fbmAccountName,
        fbmOrdersV2Api,
        handleClose,
        onError,
        onSuccess,
        orderData?.orderId,
    ]);

    const handleAllowReingestChange = (event) => {
        setAllowReingest(event.target.checked);
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            disableScrollLock={true}
            hideBackdrop={false}
        >
            <DialogTitle>Cancel Order</DialogTitle>
            <DialogContent>
                {orderData && (
                    <div style={{ marginBottom: '20px' }}>
                        <Typography variant="subtitle1">
                            <strong>MM Order ID:</strong> {orderData.orderId}
                        </Typography>
                        <Typography variant="subtitle1">
                            <strong>FBM Order Number:</strong> {orderData.fbmOrderNum}
                        </Typography>
                    </div>
                )}
                <Typography variant="body1" style={{ marginBottom: '10px' }}>
                    Please provide a reason for cancellation:
                </Typography>
                <TextField
                    autoFocus
                    margin="dense"
                    id="cancelReason"
                    label="Cancellation Reason"
                    type="text"
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    value={cancelReason}
                    onChange={handleCancelReasonChange}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={allowReingest}
                            onChange={handleAllowReingestChange}
                            name="allowReingest"
                            color="primary"
                        />
                    }
                    label="Allow Reingest"
                    style={{ marginTop: '10px' }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="secondary">
                    Cancel
                </Button>
                <Button
                    onClick={handleCancelOrder}
                    color="primary"
                    variant="contained"
                    disabled={isLoading || !cancelReason.trim()}
                >
                    {isLoading ? 'Cancelling...' : 'Confirm Cancellation'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { CancelFBMOrderModal };
